import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { AlertTriangle, RefreshCw, LogOut } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary caught an error]:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleClearAndReload = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("role");
    window.location.href = "/login";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 p-6 text-slate-200 font-sans">
          <div className="max-w-xl w-full rounded-xl border border-red-500/30 bg-slate-900/90 p-8 shadow-2xl backdrop-blur-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-500/10 text-red-500 border border-red-500/20">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-100">
                  Dashboard Component Exception
                </h1>
                <p className="text-xs text-slate-400">
                  An unexpected error occurred while rendering the dashboard.
                </p>
              </div>
            </div>

            {this.state.error && (
              <div className="mb-6 rounded-lg bg-slate-950 p-4 border border-slate-800 text-xs text-red-400 font-mono overflow-x-auto">
                <div className="font-semibold text-slate-300 mb-1">Error Message:</div>
                {this.state.error.toString()}
              </div>
            )}

            {this.state.errorInfo && (
              <details className="mb-6 text-xs text-slate-400 cursor-pointer">
                <summary className="font-semibold text-slate-400 hover:text-slate-200 transition-colors">
                  View Component Stack Trace
                </summary>
                <pre className="mt-2 p-3 bg-slate-950 rounded border border-slate-800 font-mono text-[11px] overflow-x-auto text-slate-400 max-h-48 overflow-y-auto">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="flex items-center gap-3 pt-2 border-t border-slate-800">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition-all shadow-lg shadow-indigo-600/20"
              >
                <RefreshCw size={14} /> Try Reloading Component
              </button>

              <button
                onClick={this.handleClearAndReload}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-all border border-slate-700"
              >
                <LogOut size={14} /> Re-login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

